import React from "react";
import { LockClosedIcon } from "@heroicons/react/outline";
import LineupSlot from "./LineupSlot";

/**
 * Displays the 5 slots for the primary lineup
 *
 * @param {Object} props
 * @param {Array} props.lineup - Array of 5 fighter objects or null values
 * @param {number|null} props.lotn - Fighter ID of the LOTN
 * @param {number|null} props.alternateLotn - Fighter ID of the Alternate LOTN
 * @param {Function} props.onAddFighter - Callback when user wants to add a fighter
 * @param {Function} props.onRemoveFighter - Callback when user wants to remove a fighter
 * @param {Function} props.onMoveFighter - Callback to swap a fighter to a new position (fromIndex, toIndex)
 * @param {Function} props.onToggleLotn - Callback when user toggles LOTN status
 * @param {Function} props.onToggleAlternateLotn - Callback when user toggles Alt LOTN status
 * @param {Object} props.eventData - Full event data with fights and fighters
 * @returns {JSX.Element}
 */
const LineupSlots = ({
  lineup,
  lotn,
  alternateLotn,
  onAddFighter,
  onRemoveFighter,
  onMoveFighter,
  onToggleLotn,
  onToggleAlternateLotn,
  eventData,
}) => {
  // Helper function to find opponent for a fighter
  const findOpponent = (fighterId) => {
    if (!eventData || !eventData.Fights) return null;

    for (const fight of eventData.Fights) {
      if (fight.hidden || fight.cancelled) continue;
      if (fight.Fighters && fight.Fighters.length > 1) {
        const fighterIndex = fight.Fighters.findIndex(
          (f) => f.FighterId === fighterId,
        );
        if (fighterIndex >= 0) {
          // Return the opponent (if there are 2 fighters, return the other one)
          return fight.Fighters[fighterIndex === 0 ? 1 : 0];
        }
      }
    }
    return null;
  };

  // Enhanced LOTN toggle handler
  const handleLotnToggle = (fighterId) => {
    if (lotn === fighterId) {
      // If clicking on current LOTN, just remove it
      onToggleLotn(fighterId);
    } else if (lotn && !alternateLotn) {
      // If there's already a LOTN but no Alt LOTN, make current LOTN the Alt LOTN
      const prevLotnFighter = lineup.find((f) => f && f.FighterId === lotn);
      if (prevLotnFighter) {
        onToggleAlternateLotn(prevLotnFighter);
      }
      // Then set the new fighter as LOTN
      onToggleLotn(fighterId);
    } else if (lotn) {
      // If there's already both a LOTN and Alt LOTN, replace the LOTN
      onToggleLotn(lotn); // Remove current LOTN
      onToggleLotn(fighterId); // Set new LOTN
    } else {
      // If there's no LOTN yet, simply set this fighter as LOTN
      onToggleLotn(fighterId);
    }
  };

  // Enhanced Alt LOTN toggle handler
  const handleAlternateLotnToggle = (fighter) => {
    if (alternateLotn === fighter.FighterId) {
      // If clicking on current Alt LOTN, just remove it
      onToggleAlternateLotn(fighter);
    } else if (lotn) {
      // Only allow setting Alt LOTN if there's already a LOTN
      if (alternateLotn) {
        // If there's already an Alt LOTN, replace it
        onToggleAlternateLotn(
          lineup.find((f) => f && f.FighterId === alternateLotn),
        ); // Remove current Alt LOTN
      }
      onToggleAlternateLotn(fighter); // Set new Alt LOTN
    }
  };

  return (
    <div className="space-y-4">
      {lineup.map((fighter, index) => (
        <LineupSlot
          key={index}
          position={index}
          fighter={fighter}
          opponent={fighter ? findOpponent(fighter.FighterId) : null}
          onClick={() => onAddFighter(index)}
          isLotn={fighter && fighter.FighterId === lotn}
          isAlternateLotn={fighter && fighter.FighterId === alternateLotn}
          onToggleLotn={() => fighter && handleLotnToggle(fighter.FighterId)}
          onToggleAlternateLotn={() =>
            fighter && handleAlternateLotnToggle(fighter)
          }
          onRemove={onRemoveFighter}
          onMoveUp={
            index > 0 && fighter ? () => onMoveFighter(index, index - 1) : null
          }
          onMoveDown={
            index < lineup.length - 1 && fighter
              ? () => onMoveFighter(index, index + 1)
              : null
          }
          hasExistingLotn={!!lotn && fighter && fighter.FighterId !== lotn}
        />
      ))}

      {/* LOTN reminder - improved mobile design */}
      {lineup.some((fighter) => fighter) && !lotn && (
        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg shadow-sm">
          <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <LockClosedIcon className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-amber-800 mb-1">
              Select your Lock of the Night
            </h4>
            <p className="text-xs text-amber-700 leading-relaxed">
              Tap the lock next to any fighter to make them your LOTN. They'll
              earn 2x points!
            </p>
          </div>
        </div>
      )}

      {/* Alternate LOTN reminder - improved mobile design */}
      {lineup.some((fighter) => fighter) && lotn && !alternateLotn && (
        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <LockClosedIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-blue-800 mb-1">
              Choose your Alternate LOTN
            </h4>
            <p className="text-xs text-blue-700 leading-relaxed">
              Select a backup LOTN in case your primary choice gets injured or
              pulled from the card.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineupSlots;
